import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Plus, Trash2, Pause, Play, Loader2 } from 'lucide-react';
import { api } from '../../lib/api';
import type { AnnouncementRecord, AnnouncementInput, AnnouncementPlacement } from '../../lib/types';
import { MediaPicker } from '../../components/ui/MediaPicker';
import ColorPickerInput from '../../components/ui/ColorPickerInput';

// Edita el conjunto de campañas como borrador local; cada tarjeta se guarda
// individualmente. Los cambios solo se ven en el sitio tras publicar (onSaved).
type Draft = {
  placement: AnnouncementPlacement;
  name: string;
  messageEs: string;
  messageEn: string;
  bgColor: string;
  textColor: string;
  targetUrl: string;
  ctaLabelEs: string;
  ctaLabelEn: string;
  imageUrl: string;
  expiresAt: string; // valor del <input datetime-local>, en hora local
  paused: boolean;
};

// Convierte un epoch ms a string para <input datetime-local> en hora local.
function toLocalInput(ms: number | null): string {
  if (!ms) return '';
  const d = new Date(ms);
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
}

function recordToDraft(r: AnnouncementRecord): Draft {
  return {
    placement: r.placement,
    name: r.name ?? '',
    messageEs: r.messageEs ?? '',
    messageEn: r.messageEn ?? '',
    bgColor: r.bgColor ?? '',
    textColor: r.textColor ?? '',
    targetUrl: r.targetUrl ?? '',
    ctaLabelEs: r.ctaLabelEs ?? '',
    ctaLabelEn: r.ctaLabelEn ?? '',
    imageUrl: r.imageUrl ?? '',
    expiresAt: toLocalInput(r.expiresAt),
    paused: r.paused,
  };
}

function draftToInput(d: Draft): AnnouncementInput {
  return {
    placement: d.placement,
    name: d.name || null,
    messageEs: d.messageEs || null,
    messageEn: d.messageEn || null,
    bgColor: d.bgColor || null,
    textColor: d.textColor || null,
    targetUrl: d.targetUrl || null,
    ctaLabelEs: d.ctaLabelEs || null,
    ctaLabelEn: d.ctaLabelEn || null,
    imageUrl: d.imageUrl || null,
    // datetime-local viene sin zona: lo interpretamos como hora local del navegador.
    expiresAt: d.expiresAt ? new Date(d.expiresAt).getTime() : null,
    paused: d.paused,
  };
}

const inputClass =
  'w-full bg-ui-bg border border-ui-border rounded px-3 py-2 text-sm text-ui-text focus:outline-none focus:border-ui-accent';
const labelClass = 'block text-sm text-ui-text-muted mb-1';

export default function AnnouncementsTabContent({ onSaved }: { onSaved?: () => void } = {}) {
  const [items, setItems] = useState<AnnouncementRecord[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    try {
      const rows = await api.announcements.list();
      setItems(rows);
      setDrafts(Object.fromEntries(rows.map((r) => [r.id, recordToDraft(r)])));
    } finally {
      setLoading(false);
    }
  }

  function setDraft(id: string, patch: Partial<Draft>) {
    setDrafts((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  async function create(placement: AnnouncementPlacement) {
    setBusy('create');
    try {
      const created = await api.announcements.create({ placement, name: '' });
      setItems((prev) => [...prev, created]);
      setDrafts((prev) => ({ ...prev, [created.id]: recordToDraft(created) }));
      onSaved?.();
    } finally {
      setBusy(null);
    }
  }

  async function save(id: string) {
    const draft = drafts[id];
    if (!draft) return;
    setBusy(`save:${id}`);
    try {
      const updated = await api.announcements.update(id, draftToInput(draft));
      setItems((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setDrafts((prev) => ({ ...prev, [id]: recordToDraft(updated) }));
      onSaved?.();
    } finally {
      setBusy(null);
    }
  }

  async function togglePause(id: string) {
    const draft = drafts[id];
    if (!draft) return;
    setBusy(`pause:${id}`);
    try {
      const updated = await api.announcements.update(id, { ...draftToInput(draft), paused: !draft.paused });
      setItems((prev) => prev.map((r) => (r.id === id ? updated : r)));
      setDrafts((prev) => ({ ...prev, [id]: recordToDraft(updated) }));
      onSaved?.();
    } finally {
      setBusy(null);
    }
  }

  async function remove(id: string) {
    if (!window.confirm('¿Eliminar este anuncio? No se puede deshacer.')) return;
    setBusy(`delete:${id}`);
    try {
      await api.announcements.remove(id);
      setItems((prev) => prev.filter((r) => r.id !== id));
      setDrafts((prev) => { const next = { ...prev }; delete next[id]; return next; });
      onSaved?.();
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return <p className="text-sm text-ui-text-subtle py-8 text-center">Cargando…</p>;
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap gap-2">
        <button onClick={() => create('banner')} disabled={busy === 'create'}
          className="flex items-center gap-2 px-3 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90">
          <Plus size={15} /> Nuevo banner
        </button>
        <button onClick={() => create('popup')} disabled={busy === 'create'}
          className="flex items-center gap-2 px-3 py-2 bg-ui-surface-2 border border-ui-border text-ui-text text-sm rounded-md disabled:opacity-50 hover:border-ui-accent transition-colors">
          <Plus size={15} /> Nuevo popup
        </button>
      </div>

      {items.length === 0 && (
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Megaphone size={28} className="text-ui-text-subtle" />
          <p className="text-sm text-ui-text-muted">Aún no tienes anuncios.</p>
          <p className="text-sm text-ui-text-subtle max-w-sm">
            Crea un banner para mostrar un aviso sobre el menú de tu sitio, o un popup que aparezca al entrar a la página de inicio.
          </p>
        </div>
      )}

      <AnimatePresence>
        {items.map((item, idx) => {
          const draft = drafts[item.id];
          if (!draft) return null;
          const isPopup = item.placement === 'popup';
          const expired = item.expiresAt != null && item.expiresAt < Date.now();
          return (
            <motion.div key={item.id}
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, delay: idx * 0.04 }}
              className="rounded-lg border border-ui-border bg-ui-surface overflow-hidden">

              {/* Cabecera de la tarjeta */}
              <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ui-border">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-sm ${isPopup ? 'bg-ui-accent/15 text-ui-accent' : 'bg-ui-surface-2 text-ui-text-muted'}`}>
                    {isPopup ? 'Popup de inicio' : 'Banner superior'}
                  </span>
                  {draft.paused && <span className="shrink-0 text-sm text-ui-text-subtle">En pausa</span>}
                  {expired && !draft.paused && <span className="shrink-0 text-sm text-amber-400">Caducado</span>}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => togglePause(item.id)} disabled={busy === `pause:${item.id}`}
                    title={draft.paused ? 'Reanudar' : 'Pausar'}
                    className="p-2 rounded-md text-ui-text-muted hover:text-ui-text hover:bg-ui-surface-2 transition-colors disabled:opacity-50">
                    {draft.paused ? <Play size={15} /> : <Pause size={15} />}
                  </button>
                  <button onClick={() => remove(item.id)} disabled={busy === `delete:${item.id}`}
                    title="Eliminar"
                    className="p-2 rounded-md text-ui-text-muted hover:text-red-400 hover:bg-ui-surface-2 transition-colors disabled:opacity-50">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {/* Cuerpo del formulario */}
              <div className="p-4 space-y-4">
                <div>
                  <label className={labelClass}>Nombre interno <span className="text-ui-text-subtle">(solo lo ves tú)</span></label>
                  <input value={draft.name} onChange={(e) => setDraft(item.id, { name: e.target.value })}
                    placeholder="Ej. Oferta de verano" className={inputClass} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Mensaje (español)</label>
                    <textarea value={draft.messageEs} onChange={(e) => setDraft(item.id, { messageEs: e.target.value })}
                      rows={2} placeholder="20% de descuento solo esta semana"
                      className={`${inputClass} resize-none`} />
                  </div>
                  <div>
                    <label className={labelClass}>Mensaje (inglés)</label>
                    <textarea value={draft.messageEn} onChange={(e) => setDraft(item.id, { messageEn: e.target.value })}
                      rows={2} placeholder="20% off this week only"
                      className={`${inputClass} resize-none`} />
                  </div>
                </div>

                {isPopup && (
                  <div>
                    <label className={labelClass}>Imagen <span className="text-ui-text-subtle">(opcional — anuncio o código promocional)</span></label>
                    <MediaPicker value={draft.imageUrl ? [draft.imageUrl] : []}
                      onChange={(urls) => setDraft(item.id, { imageUrl: urls[0] ?? '' })}
                      multiple={false} folder="settings" variant="large" />
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Color de fondo</label>
                    <ColorPickerInput value={draft.bgColor} onChange={(v) => setDraft(item.id, { bgColor: v })} placeholder="#1a1a2e" />
                  </div>
                  <div>
                    <label className={labelClass}>Color del texto</label>
                    <ColorPickerInput value={draft.textColor} onChange={(v) => setDraft(item.id, { textColor: v })} placeholder="#ffffff" />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Enlace al hacer clic <span className="text-ui-text-subtle">(opcional)</span></label>
                  <input value={draft.targetUrl} onChange={(e) => setDraft(item.id, { targetUrl: e.target.value })}
                    placeholder="https://… o /promociones" className={inputClass} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Texto del botón (español)</label>
                    <input value={draft.ctaLabelEs} onChange={(e) => setDraft(item.id, { ctaLabelEs: e.target.value })}
                      placeholder="Ver más" className={inputClass} />
                  </div>
                  <div>
                    <label className={labelClass}>Texto del botón (inglés)</label>
                    <input value={draft.ctaLabelEn} onChange={(e) => setDraft(item.id, { ctaLabelEn: e.target.value })}
                      placeholder="Learn more" className={inputClass} />
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Se deja de mostrar el <span className="text-ui-text-subtle">(opcional)</span></label>
                  <input type="datetime-local" value={draft.expiresAt}
                    onChange={(e) => setDraft(item.id, { expiresAt: e.target.value })}
                    className={`${inputClass} max-w-xs`} />
                  <p className="text-sm text-ui-text-subtle mt-1">Después de esta fecha y hora, el anuncio desaparece solo.</p>
                </div>

                <div className="pt-1">
                  <button onClick={() => save(item.id)} disabled={busy === `save:${item.id}`}
                    className="flex items-center gap-2 px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm rounded-md disabled:opacity-50 hover:opacity-90">
                    {busy === `save:${item.id}` && <Loader2 size={14} className="animate-spin" />}
                    {busy === `save:${item.id}` ? 'Guardando…' : 'Guardar anuncio'}
                  </button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
